import React, { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Admin screen: assign each project a Support Manager, Customer Manager and support
 * teams, toggle per-customer task breakdown, and reassign a project's manager
 * (cascading to its open tickets).
 */
export default function Assignments() {
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [assignMap, setAssignMap] = useState({});   // projectId -> View
  const [configMap, setConfigMap] = useState({});   // companyId -> config
  const [editing, setEditing] = useState(null);     // project being edited
  const [form, setForm] = useState({});
  const [reassign, setReassign] = useState(null);   // { project, supportManager }
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    const [p, c, u, t, a, cfg] = await Promise.all([
      api.get('/projects').catch(() => ({ data: [] })),
      api.get('/companies').catch(() => ({ data: [] })),
      api.get('/users/lookup').catch(() => ({ data: [] })),
      api.get('/teams').catch(() => ({ data: [] })),
      api.get('/project-assignments').catch(() => ({ data: [] })),
      api.get('/customer-configs').catch(() => ({ data: [] })),
    ]);
    setProjects(p.data); setCompanies(c.data); setUsers(u.data); setTeams(t.data);
    const am = {}; a.data.forEach(x => { am[x.projectId] = x; }); setAssignMap(am);
    const cm = {}; cfg.data.forEach(x => { cm[x.companyId] = x; }); setConfigMap(cm);
  };
  useEffect(() => { load(); }, []);

  const coName = (id) => companies.find(x => x.id === id)?.name || '—';
  const uName = (username) => { const u = users.find(x => x.username === username); return u ? u.fullName : (username || '—'); };
  const teamName = (id) => teams.find(x => x.id === id)?.name || `#${id}`;
  const mains = teams.filter(t => !t.parentId);
  const subsOf = (id) => teams.filter(t => t.parentId === id);

  const openEdit = (p) => {
    const a = assignMap[p.id] || {};
    setEditing(p);
    setForm({
      supportManager: a.supportManager || '',
      customerManager: a.customerManager || '',
      teamIds: a.teamIds || [],
    });
    setErr('');
  };

  const toggleTeam = (id) => setForm(f => ({
    ...f, teamIds: f.teamIds.includes(id) ? f.teamIds.filter(x => x !== id) : [...f.teamIds, id],
  }));

  const save = async () => {
    setErr('');
    try {
      await api.put(`/project-assignments/${editing.id}`, {
        projectId: editing.id,
        companyId: editing.companyId || null,
        supportManager: form.supportManager || null,
        customerManager: form.customerManager || null,
        teamIds: form.teamIds,
      });
      setEditing(null); setMsg('Assignment saved'); setTimeout(() => setMsg(''), 2500); load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
  };

  const doReassign = async () => {
    setErr('');
    try {
      const r = await api.post(`/project-assignments/${reassign.project.id}/reassign-manager`, {
        supportManager: reassign.supportManager || '',
      });
      setReassign(null);
      setMsg(`Manager reassigned · ${r.data.ticketsReassigned} ticket(s) updated`);
      setTimeout(() => setMsg(''), 3500); load();
    } catch (e) { setErr(e.response?.data?.error || 'Reassign failed'); }
  };

  const toggleTaskBreakdown = async (companyId, enabled) => {
    if (!companyId) return;
    await api.put(`/customer-configs/${companyId}`, { companyId, taskBreakdownEnabled: enabled });
    load();
  };

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Project Assignments</h1>
      <p style={{ color: '#64748b', marginBottom: 16 }}>Assign a Support Manager, Customer Manager and teams to each project</p>
      {msg && <div style={{ background: '#dcfce7', color: '#15803d', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{msg}</div>}

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table>
          <thead><tr><th>Project</th><th>Customer</th><th>Support Manager</th><th>Customer Manager</th><th>Teams</th><th>Task breakdown</th><th></th></tr></thead>
          <tbody>
            {projects.length === 0 && <tr><td colSpan={7} style={{ color: '#94a3b8', fontSize: 13 }}>No projects.</td></tr>}
            {projects.map(p => {
              const a = assignMap[p.id] || {};
              const cfg = configMap[p.companyId];
              return (
                <tr key={p.id}>
                  <td style={{ fontWeight: 600 }}>{p.projectName}<div style={{ fontSize: 11, color: '#94a3b8' }}>{p.projectCode}</div></td>
                  <td>{coName(p.companyId)}</td>
                  <td>{uName(a.supportManager)}</td>
                  <td>{a.customerManager ? uName(a.customerManager) : '—'}</td>
                  <td style={{ maxWidth: 220 }}>
                    {(a.teamIds && a.teamIds.length) ? a.teamIds.map(id => (
                      <span key={id} style={{ display: 'inline-block', fontSize: 11, background: 'var(--gray-100)', borderRadius: 999, padding: '2px 10px', margin: 2 }}>{teamName(id)}</span>
                    )) : <span style={{ color: '#94a3b8' }}>—</span>}
                  </td>
                  <td>
                    <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center', color: p.companyId ? '#0f172a' : '#cbd5e1' }} title={p.companyId ? '' : 'Project has no customer'}>
                      <input type="checkbox" disabled={!p.companyId} checked={!!cfg?.taskBreakdownEnabled} onChange={e => toggleTaskBreakdown(p.companyId, e.target.checked)} style={{ width: 'auto' }} />
                      {cfg?.taskBreakdownEnabled ? 'On' : 'Off'}
                    </label>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(p)}>Assign</button>
                    <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => { setReassign({ project: p, supportManager: a.supportManager || '' }); setErr(''); }}>Reassign mgr</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Assign modal */}
      {editing && (
        <div style={modalBg} onClick={() => setEditing(null)}>
          <div style={{ ...modalBox, width: 460 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, marginBottom: 2, color: '#0f172a' }}>{editing.projectName}</h3>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 18 }}>{editing.projectCode} · {coName(editing.companyId)}</p>
            {err && <div style={errBox}>{err}</div>}

            <Field label="Support Manager">
              <select value={form.supportManager} onChange={e => setForm({ ...form, supportManager: e.target.value })}>
                <option value="">— None —</option>
                {users.map(u => <option key={u.username} value={u.username}>{u.fullName} ({u.username})</option>)}
              </select>
            </Field>
            <Field label="Customer Manager">
              <select value={form.customerManager} onChange={e => setForm({ ...form, customerManager: e.target.value })}>
                <option value="">— None —</option>
                {users.map(u => <option key={u.username} value={u.username}>{u.fullName} ({u.username})</option>)}
              </select>
            </Field>
            <Field label="Teams">
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 10, padding: 10, maxHeight: 220, overflowY: 'auto' }}>
                {mains.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13 }}>No teams defined.</div>}
                {mains.map(m => (
                  <div key={m.id} style={{ marginBottom: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="checkbox" checked={form.teamIds.includes(m.id)} onChange={() => toggleTeam(m.id)} style={{ width: 'auto' }} />{m.name}
                    </label>
                    {subsOf(m.id).map(s => (
                      <label key={s.id} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', paddingLeft: 24 }}>
                        <input type="checkbox" checked={form.teamIds.includes(s.id)} onChange={() => toggleTeam(s.id)} style={{ width: 'auto' }} />↳ {s.name}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn-primary" onClick={save} style={{ flex: 1 }}>Save</button>
              <button className="btn-ghost" onClick={() => setEditing(null)} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign manager modal */}
      {reassign && (
        <div style={modalBg} onClick={() => setReassign(null)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, marginBottom: 4, color: '#0f172a' }}>Reassign Support Manager</h3>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{reassign.project.projectName}. The new manager takes over <strong>all open tickets</strong> of this project.</p>
            {err && <div style={errBox}>{err}</div>}
            <Field label="New Support Manager">
              <select value={reassign.supportManager} onChange={e => setReassign({ ...reassign, supportManager: e.target.value })}>
                <option value="">— None —</option>
                {users.map(u => <option key={u.username} value={u.username}>{u.fullName} ({u.username})</option>)}
              </select>
            </Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn-primary" onClick={doReassign} style={{ flex: 1 }}>Reassign</button>
              <button className="btn-ghost" onClick={() => setReassign(null)} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const modalBg = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 };
const modalBox = { background: '#fff', borderRadius: 14, padding: 28, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const errBox = { background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 };

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4, color: '#374151' }}>{label}</label>
      {children}
    </div>
  );
}
